import {
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

import Navbar from "../navbar/Navbar";
import Collections from "./collections/Collections";
import Features from "./Feature/Features";
import Shapingd from "./shaping/ Shapingd";
import Footer from "../Footer/Footer";
import ExploreCollection from "./exploreCollection/ExploreCollection";
import "./Home.css";

const DEFAULT_YEARS = [2027, 2026];

const DEFAULT_CATEGORIES = [
	"Bridal 2027",
	"Prom 2027",
	"Evening 2027",
];

const HOME_PRODUCTS_CACHE_KEY = "monsini-home-products-v4";
const HOME_PRODUCTS_CACHE_TTL = 10 * 60 * 1000;
const HOME_REQUEST_TIMEOUT = 25000;
const HERO_START_DELAY = 900;
const HERO_PRELOAD_DELAY = 300;
const HERO_INTERVAL = 4500;
const HERO_FADE_DURATION = 300;

/* =========================================================
   HERO IMAGES
   ========================================================= */

/*
	SLIDE 1

	All 3 images below will show together.
*/
const IMAGES1 = [
	"https://admin.monsinidress.com/uploads/Monsini/2027/WI27/main/B50307/B50307.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/WI27/main/B50301/B50301.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/WI27/main/B50310/B50310.jpg",
];

/*
	SLIDE 2

	All 3 images below will show together.
*/
const IMAGES2 = [
	"https://admin.monsinidress.com/uploads/Monsini/2027/SU27/main/E40252/E40252.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/SU27/main/E40254/E40254.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/SU27/main/E40256/E40256.jpg",
];

/*
	SLIDE 3

	All 3 images below will show together.
*/
const IMAGES3 = [
	"https://admin.monsinidress.com/uploads/Monsini/2027/FA27/main/50025/50025.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/FA27/main/50046/50046.jpg",
	"https://admin.monsinidress.com/uploads/Monsini/2027/FA27/main/60001/60001.jpg",
];

/*
	IMPORTANT:

	Slide 1 = IMAGES1[0], IMAGES1[1], IMAGES1[2]
	Slide 2 = IMAGES2[0], IMAGES2[1], IMAGES2[2]
	Slide 3 = IMAGES3[0], IMAGES3[1], IMAGES3[2]
*/
const HERO_IMAGE_GROUPS = [
	IMAGES1,
	IMAGES2,
	IMAGES3,
];

const TEXTS1 = [
	"Enchanted Bridal",
	"Enchanted Evening",
	"Monsini Prom",
];

const LINKS = [
	"/bridal/",
	"/evening/",
	"/prom/",
];

/*
	All hero slides use 2027.
*/
const HERO_YEARS = [
	2027,
	2027,
	2027,
];

/* =========================================================
   HERO RESPONSIVE COLUMNS
   ========================================================= */

function getHeroColumnCount() {
	if (
		typeof window === "undefined" ||
		!window.matchMedia
	) {
		return 3;
	}

	if (
		window.matchMedia("(max-width: 785px)").matches
	) {
		return 1;
	}

	if (
		window.matchMedia("(max-width: 1024px)").matches
	) {
		return 2;
	}

	return 3;
}

/* =========================================================
   NORMALIZE YEARS
   ========================================================= */

function normalizeYearValues(values) {
	if (!Array.isArray(values)) {
		return [];
	}

	return [
		...new Set(
			values
				.map(Number)
				.filter(Number.isFinite)
		),
	].sort((a, b) => b - a);
}

/* =========================================================
   HOME PRODUCTS
   ========================================================= */

function pickHomeProducts(products) {
	if (!Array.isArray(products)) {
		return [];
	}

	const visibleProducts = products.filter((item) => {
		const value = item?.viewInfront;

		return (
			value === true ||
			String(value).toLowerCase() === "true" ||
			value === 1
		);
	});

	const source =
		visibleProducts.length > 0
			? visibleProducts
			: products;

	const selected = [];
	const seen = new Set();

	const addItems = (items, max) => {
		let added = 0;

		for (const item of items) {
			if (added >= max) {
				break;
			}

			const key =
				item?._id ||
				item?.id ||
				`${item?.style || "style"}-${item?.year || "year"}`;

			if (seen.has(key)) {
				continue;
			}

			seen.add(key);
			selected.push(item);

			added += 1;
		}
	};

	addItems(
		source.slice(0, 12),
		12
	);

	addItems(
		source.filter((item) =>
			String(item?.category || "")
				.toLowerCase()
				.includes("prom")
		),
		16
	);

	addItems(
		source.filter((item) =>
			String(item?.category || "")
				.toLowerCase()
				.includes("evening")
		),
		16
	);

	addItems(
		source.filter((item) =>
			String(item?.category || "")
				.toLowerCase()
				.includes("bridal")
		),
		20
	);

	return selected.slice(0, 64);
}

/* =========================================================
   SESSION CACHE
   ========================================================= */

function readHomeCache() {
	if (typeof window === "undefined") {
		return null;
	}

	try {
		const raw =
			window.sessionStorage.getItem(
				HOME_PRODUCTS_CACHE_KEY
			);

		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw);

		if (
			!parsed?.savedAt ||
			Date.now() - parsed.savedAt >
				HOME_PRODUCTS_CACHE_TTL
		) {
			window.sessionStorage.removeItem(
				HOME_PRODUCTS_CACHE_KEY
			);

			return null;
		}

		if (!Array.isArray(parsed.products)) {
			return null;
		}

		return parsed;
	} catch {
		return null;
	}
}

function writeHomeCache(payload) {
	if (typeof window === "undefined") {
		return;
	}

	try {
		window.sessionStorage.setItem(
			HOME_PRODUCTS_CACHE_KEY,
			JSON.stringify({
				...payload,
				savedAt: Date.now(),
			})
		);
	} catch {
		// Website still works if sessionStorage is unavailable.
	}
}

/* =========================================================
   HERO IMAGE
   ========================================================= */

const MemoizedImage = memo(
	function MemoizedImage({
		src,
		alt,
		className,
		priority = false,
	}) {
		return (
			<img
				src={src}
				alt={alt}
				className={className}
				loading={
					priority
						? "eager"
						: "lazy"
				}
				fetchPriority={
					priority
						? "high"
						: "low"
				}
				decoding="async"
				width="720"
				height="960"
			/>
		);
	}
);

/* =========================================================
   HERO TEXT
   ========================================================= */

const MemoizedTextOverlay = memo(
	function MemoizedTextOverlay({
		currentImageIndex,
		fade,
		slideYear,
	}) {
		const collectionName =
			TEXTS1[currentImageIndex] ||
			"Monsini";

		const route =
			`${LINKS[currentImageIndex] || "/"}${slideYear}`;

		return (
			<div
				className={`px-5 overlay-inside font-Jost-Regular ${
					fade
						? "opacity-100"
						: "opacity-0"
				}`}
			>
				<Link
					to={route}
					aria-label={`Explore ${collectionName} ${slideYear} Collection`}
				>
					<div
						className={`text-container font-lora tron ${
							fade
								? "troff"
								: ""
						}`}
					>
						<h2>
							{collectionName}
						</h2>

						<h2>
							{slideYear} Collection
						</h2>

						<span className="inline-block">
							Explore More
						</span>
					</div>
				</Link>
			</div>
		);
	}
);

/* =========================================================
   PRODUCTS LOADER
   ========================================================= */

const HomeProductsLoader = memo(
	function HomeProductsLoader() {
		return (
			<section
				className="perf-defer-section px-4 py-14 md:px-10"
				role="status"
				aria-live="polite"
				aria-label="Loading latest Monsini products"
			>
				<div className="mb-8 flex items-center justify-center gap-3 text-gray-600">
					<div
						className="h-7 w-7 animate-spin rounded-full border-4 border-gray-200 border-t-pink-700"
						aria-hidden="true"
					/>

					<span className="font-poppins text-base font-medium">
						Loading latest collections...
					</span>
				</div>

				<div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-4">
					{[0, 1, 2, 3].map(
						(item) => (
							<div
								key={item}
								className="aspect-[4/5] animate-pulse rounded-xl bg-gray-100"
								aria-hidden="true"
							/>
						)
					)}
				</div>
			</section>
		);
	}
);

/* =========================================================
   HOME
   ========================================================= */

const Home = () => {
	const [data, setData] =
		useState([]);

	const [years, setYears] =
		useState(DEFAULT_YEARS);

	const [
		categories,
		setCategories,
	] = useState(
		DEFAULT_CATEGORIES
	);

	const [
		loadingProducts,
		setLoadingProducts,
	] = useState(true);

	const [error, setError] =
		useState(null);

	const [
		retryKey,
		setRetryKey,
	] = useState(0);

	/* =====================================================
	   HERO STATES
	   ===================================================== */

	const [
		currentImageIndex,
		setCurrentImageIndex,
	] = useState(0);

	const [fade, setFade] =
		useState(true);

	const [
		nextSlideReady,
		setNextSlideReady,
	] = useState(false);

	const [
		sliderEnabled,
		setSliderEnabled,
	] = useState(false);

	const [
		heroColumns,
		setHeroColumns,
	] = useState(
		getHeroColumnCount
	);

	/* =====================================================
	   REFS
	   ===================================================== */

	const mountedRef =
		useRef(true);

	const productsTriggerRef =
		useRef(null);

	const productsFetchedRef =
		useRef(false);

	const preloadedSlideColumnsRef =
		useRef(new Map());

	const transitionTimeoutRef =
		useRef(null);

	/* =====================================================
	   CACHE
	   ===================================================== */

	useEffect(() => {
		mountedRef.current = true;

		const cached =
			readHomeCache();

		if (cached) {
			setData(
				cached.products
			);

			if (
				Array.isArray(
					cached.years
				) &&
				cached.years.length > 0
			) {
				setYears(
					cached.years
				);
			}

			if (
				Array.isArray(
					cached.categories
				) &&
				cached.categories.length >
					0
			) {
				setCategories(
					cached.categories
				);
			}

			setLoadingProducts(false);

			productsFetchedRef.current =
				true;
		}

		return () => {
			mountedRef.current =
				false;

			if (
				transitionTimeoutRef.current
			) {
				window.clearTimeout(
					transitionTimeoutRef.current
				);
			}
		};
	}, []);

	/* =====================================================
	   RESPONSIVE HERO
	   ===================================================== */

	useEffect(() => {
		const mobileQuery = window.matchMedia("(max-width: 785px)");
		const tabletQuery = window.matchMedia("(max-width: 1024px)");

		const updateHeroColumns = () => {
			setHeroColumns(getHeroColumnCount());
		};

		updateHeroColumns();

		if (mobileQuery.addEventListener) {
			mobileQuery.addEventListener("change", updateHeroColumns);
			tabletQuery.addEventListener("change", updateHeroColumns);
		} else {
			mobileQuery.addListener(updateHeroColumns);
			tabletQuery.addListener(updateHeroColumns);
		}

		return () => {
			if (mobileQuery.removeEventListener) {
				mobileQuery.removeEventListener("change", updateHeroColumns);
				tabletQuery.removeEventListener("change", updateHeroColumns);
			} else {
				mobileQuery.removeListener(updateHeroColumns);
				tabletQuery.removeListener(updateHeroColumns);
			}
		};
	}, []);

	/* =====================================================
	   PRODUCTS API
	   ===================================================== */

	useEffect(() => {
		const target =
			productsTriggerRef.current;

		if (
			!target ||
			productsFetchedRef.current
		) {
			return undefined;
		}

		let requestController =
			null;

		let requestTimeout =
			null;

		let didTimeout =
			false;

		const fetchProducts =
			async () => {
				if (
					productsFetchedRef.current
				) {
					return;
				}

				productsFetchedRef.current =
					true;

				setLoadingProducts(
					true
				);

				setError(null);

				requestController =
					new AbortController();

				requestTimeout =
					window.setTimeout(
						() => {
							didTimeout =
								true;

							requestController?.abort();
						},
						HOME_REQUEST_TIMEOUT
					);

				try {
					const response =
						await fetch(
							"https://admin.monsinidress.com/api/products/brand",
							{
								method: "POST",

								headers: {
									"Content-Type":
										"application/json",
								},

								body: JSON.stringify(
									{
										brand:
											"Monsini",
									}
								),

								signal:
									requestController
										.signal,
							}
						);

					if (
						!response.ok
					) {
						throw new Error(
							`Products request failed: ${response.status}`
						);
					}

					const result =
						await response.json();

					if (
						!mountedRef.current
					) {
						return;
					}

					const apiData =
						result?.data;

					const homeProducts =
						pickHomeProducts(
							apiData?.products
						);

					const apiYears =
						normalizeYearValues(
							apiData?.years
						);

					const derivedYears =
						normalizeYearValues(
							homeProducts.map(
								(
									item
								) =>
									item?.year
							)
						);

					const nextYears =
						apiYears.length >
						0
							? apiYears
							: derivedYears;

					const nextCategories =
						Array.isArray(
							apiData?.categories
						) &&
						apiData
							.categories
							.length > 0
							? apiData.categories
							: DEFAULT_CATEGORIES;

					setData(
						homeProducts
					);

					if (
						nextYears.length >
						0
					) {
						setYears(
							nextYears
						);
					}

					setCategories(
						nextCategories
					);

					writeHomeCache({
						products:
							homeProducts,

						years:
							nextYears.length >
							0
								? nextYears
								: DEFAULT_YEARS,

						categories:
							nextCategories,
					});
				} catch (
					fetchError
				) {
					if (
						!mountedRef.current
					) {
						return;
					}

					if (
						fetchError?.name ===
						"AbortError"
					) {
						if (
							didTimeout
						) {
							setError(
								"The latest products are taking longer than expected. Please try again."
							);
						}

						return;
					}

					setError(
						"Unable to load the latest products. Please try again."
					);
				} finally {
					if (
						requestTimeout
					) {
						window.clearTimeout(
							requestTimeout
						);
					}

					if (
						mountedRef.current
					) {
						setLoadingProducts(
							false
						);
					}
				}
			};

		if (
			!(
				"IntersectionObserver" in
				window
			)
		) {
			const fallbackTimer =
				window.setTimeout(
					fetchProducts,
					500
				);

			return () => {
				window.clearTimeout(
					fallbackTimer
				);

				requestController?.abort();

				if (
					requestTimeout
				) {
					window.clearTimeout(
						requestTimeout
					);
				}
			};
		}

		const observer =
			new IntersectionObserver(
				(entries) => {
					if (
						entries[0]
							?.isIntersecting
					) {
						fetchProducts();

						observer.disconnect();
					}
				},
				{
					rootMargin:
						"400px 0px",

					threshold:
						0.01,
				}
			);

		observer.observe(
			target
		);

		return () => {
			observer.disconnect();

			requestController?.abort();

			if (
				requestTimeout
			) {
				window.clearTimeout(
					requestTimeout
				);
			}
		};
	}, [retryKey]);

	/* =====================================================
	   RETRY
	   ===================================================== */

	const retryProducts =
		useCallback(() => {
			productsFetchedRef.current =
				false;

			setError(null);

			setLoadingProducts(
				true
			);

			setRetryKey(
				(value) =>
					value + 1
			);
		}, []);

	/* =====================================================
	   ENABLE HERO SLIDER AUTOMATICALLY
	   ===================================================== */

	useEffect(() => {
		const reducedMotionQuery = window.matchMedia(
			"(prefers-reduced-motion: reduce)"
		);

		if (reducedMotionQuery.matches) {
			return undefined;
		}

		let startTimer = null;

		const startSlider = () => {
			if (document.visibilityState !== "hidden") {
				setSliderEnabled(true);
			}
		};

		const scheduleStart = () => {
			window.clearTimeout(startTimer);
			startTimer = window.setTimeout(startSlider, HERO_START_DELAY);
		};

		if (document.readyState === "complete") {
			scheduleStart();
		} else {
			window.addEventListener("load", scheduleStart, { once: true });
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				setSliderEnabled(false);
				return;
			}

			scheduleStart();
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.clearTimeout(startTimer);
			window.removeEventListener("load", scheduleStart);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	/* =====================================================
	   NEXT SLIDE
	   ===================================================== */

	const getNextSlideIndex =
		useCallback(
			(currentIndex) =>
				currentIndex ===
				HERO_IMAGE_GROUPS.length -
					1
					? 0
					: currentIndex +
						1,
			[]
		);

	/* =====================================================
	   PRELOAD ONLY IMAGES NEEDED FOR THIS VIEWPORT
	   ===================================================== */

	const preloadSlide = useCallback((slideIndex, columnCount) => {
		const slideImages = (HERO_IMAGE_GROUPS[slideIndex] || []).slice(
			0,
			Math.max(1, columnCount)
		);

		return Promise.all(
			slideImages.map(
				(src) =>
					new Promise((resolve) => {
						const image = new Image();
						let settled = false;

						const finish = (loaded) => {
							if (settled) return;
							settled = true;
							resolve(loaded);
						};

						image.onload = () => finish(true);
						image.onerror = () => finish(false);
						image.decoding = "async";
						image.src = src;

						if (image.complete) {
							finish(image.naturalWidth > 0);
						}
					})
			)
		);
	}, []);

	const preloadNextSlide = useCallback(
		async (slideIndex, columnCount) => {
			const alreadyPreloaded =
				preloadedSlideColumnsRef.current.get(slideIndex) || 0;

			if (alreadyPreloaded >= columnCount) {
				setNextSlideReady(true);
				return;
			}

			await preloadSlide(slideIndex, columnCount);

			if (!mountedRef.current) {
				return;
			}

			preloadedSlideColumnsRef.current.set(
				slideIndex,
				Math.max(alreadyPreloaded, columnCount)
			);
			setNextSlideReady(true);
		},
		[preloadSlide]
	);

	/* =====================================================
	   PRELOAD NEXT HERO AFTER THE FIRST VIEW HAS SETTLED
	   ===================================================== */

	useEffect(() => {
		if (!sliderEnabled) {
			return undefined;
		}

		setNextSlideReady(false);

		const nextIndex = getNextSlideIndex(currentImageIndex);
		const timer = window.setTimeout(() => {
			preloadNextSlide(nextIndex, heroColumns);
		}, HERO_PRELOAD_DELAY);

		return () => window.clearTimeout(timer);
	}, [
		sliderEnabled,
		currentImageIndex,
		heroColumns,
		getNextSlideIndex,
		preloadNextSlide,
	]);

	/* =====================================================
	   CHANGE SLIDE
	   ===================================================== */

	const transitionToNextSlide =
		useCallback(() => {
			if (
				!sliderEnabled ||
				!nextSlideReady
			) {
				return;
			}

			setFade(false);

			if (
				transitionTimeoutRef.current
			) {
				window.clearTimeout(
					transitionTimeoutRef.current
				);
			}

			transitionTimeoutRef.current =
				window.setTimeout(
					() => {
						setCurrentImageIndex(
							(
								current
							) =>
								getNextSlideIndex(
									current
								)
						);

						setFade(
							true
						);

						setNextSlideReady(
							false
						);
					},
					HERO_FADE_DURATION
				);
		}, [
			sliderEnabled,
			nextSlideReady,
			getNextSlideIndex,
		]);

	/* =====================================================
	   AUTO SLIDER
	   ===================================================== */

	useEffect(() => {
		if (!sliderEnabled || !nextSlideReady) {
			return undefined;
		}

		const timer = window.setTimeout(
			transitionToNextSlide,
			HERO_INTERVAL
		);

		return () => window.clearTimeout(timer);
	}, [sliderEnabled, nextSlideReady, transitionToNextSlide]);

	/* =====================================================
	   CURRENT HERO DATA
	   ===================================================== */

	const numericYears =
		normalizeYearValues(
			years
		);

	const latestYear =
		numericYears.length > 0
			? numericYears[0]
			: 2027;

	const slideYear =
		HERO_YEARS[
			currentImageIndex
		] || 2027;

	/*
		This is the important part.

		Slide 1:
			currentSlideImages = IMAGES1

		Slide 2:
			currentSlideImages = IMAGES2

		Slide 3:
			currentSlideImages = IMAGES3
	*/
	const currentSlideImages =
		HERO_IMAGE_GROUPS[
			currentImageIndex
		] || IMAGES1;

	/* =====================================================
	   RENDER
	   ===================================================== */

	return (
		<div className="relative overflow-hidden">
			<Navbar />

			<main id="main-content">

				{/* =========================================
				    HERO
				    ========================================= */}

				<section
					className="image-container mt-24"
					aria-label="Featured Monsini collections"
				>
					<div className="text-overlay">
						<MemoizedTextOverlay
							currentImageIndex={
								currentImageIndex
							}
							fade={fade}
							slideYear={
								slideYear
							}
						/>
					</div>

					{/* LEFT IMAGE */}

					<div className="image-set im1">
						<MemoizedImage
							src={
								currentSlideImages[0]
							}
							alt={`${TEXTS1[currentImageIndex]} ${slideYear} collection`}
							className={`image ${
								fade
									? "fade-in"
									: "fade-out"
							}`}
							priority
						/>
					</div>

					{/* CENTER IMAGE */}

					{heroColumns >= 2 && (
						<div className="image-set m2">
							<MemoizedImage
								src={
									currentSlideImages[1]
								}
								alt=""
								className={`image ${
									fade
										? "fade-in"
										: "fade-out"
								}`}
							/>
						</div>
					)}

					{/* RIGHT IMAGE */}

					{heroColumns >= 3 && (
						<div className="image-set m3">
							<MemoizedImage
								src={
									currentSlideImages[2]
								}
								alt=""
								className={`image ${
									fade
										? "fade-in"
										: "fade-out"
								}`}
							/>
						</div>
					)}
				</section>

				{/* =========================================
				    EXPLORE
				    ========================================= */}

				<div className="perf-defer-section">
					<ExploreCollection
						years={
							latestYear
						}
					/>
				</div>

				{/* =========================================
				    PRODUCTS TRIGGER
				    ========================================= */}

				<div
					id="collection"
					ref={
						productsTriggerRef
					}
					aria-hidden="true"
					className="h-px w-full scroll-mt-28"
				/>

				{/* =========================================
				    LOADING
				    ========================================= */}

				{loadingProducts &&
					data.length ===
						0 && (
						<HomeProductsLoader />
					)}

				{/* =========================================
				    ERROR
				    ========================================= */}

				{error &&
					data.length ===
						0 && (
						<div
							role="alert"
							className="perf-defer-section mx-auto mb-10 max-w-2xl rounded-xl border border-yellow-300 bg-yellow-50 px-5 py-5 text-center text-yellow-900"
						>
							<p className="font-poppins">
								{
									error
								}
							</p>

							<button
								type="button"
								onClick={
									retryProducts
								}
								className="mt-4 rounded-md bg-[#072349] px-5 py-2.5 font-poppins font-semibold text-white transition hover:bg-[#0b356d]"
							>
								Try
								Again
							</button>
						</div>
					)}

				{/* =========================================
				    PRODUCTS
				    ========================================= */}

				{data.length > 0 && (
					<>
						<div className="perf-defer-section">
							<Collections
								data={
									data
								}
								years={
									years
								}
								categories={
									categories
								}
							/>
						</div>

						<div className="perf-defer-section">
							<Features
								data={
									data
								}
							/>
						</div>
					</>
				)}

				{/* =========================================
				    SHAPING
				    ========================================= */}

				<div className="perf-defer-section">
					<Shapingd />
				</div>
			</main>

			{/* =============================================
			    FOOTER
			    ============================================= */}

			<div className="perf-defer-section">
				<Footer />
			</div>
		</div>
	);
};

export default memo(Home);
